import { type ReactNode, use } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/shadcn/utils';
import { PortalSidePanelSlot } from '@/modules/layouts/components/portalSidePanelSlot';

interface PortalSidePanelProps extends React.ComponentProps<'aside'> {
  children: ReactNode;
  /** Tapping the page beside the covering panel (below `lg`) closes it. */
  onClose: () => void;
}

/**
 * A panel beside the page instead of over it. From `lg` it takes its width
 * from the page body, which keeps scrolling on its own, so nothing on the
 * page hides under it (a wide table scrolls sideways instead). Below that
 * there is no room for both, so it covers the page: it slides in over a
 * scrim. Beside the page it takes 40% of the room right of the sidebar, so it
 * grows when the sidebar collapses. It appears at once; it opens too often
 * to animate, and growing its width would reflow the table every frame.
 * Renders nothing outside the portal layout.
 */
export function PortalSidePanel({
  children,
  className,
  onClose,
  ...props
}: PortalSidePanelProps) {
  const slot = use(PortalSidePanelSlot);
  if (!slot) return null;

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-40 bg-black/20 transition-opacity duration-200 lg:hidden starting:opacity-0"
        onClick={onClose}
      />
      <aside
        className={cn(
          'bg-background ease-drawer fixed inset-y-0 right-0 z-40 flex w-full flex-col border-l shadow-xl outline-none sm:max-w-[480px] lg:static lg:z-auto lg:w-[clamp(380px,40%,640px)] lg:max-w-none lg:shrink-0 lg:shadow-none',
          'transition-[translate,opacity] duration-300 lg:transition-none starting:translate-x-full motion-reduce:starting:translate-x-0 motion-reduce:starting:opacity-0',
          className,
        )}
        {...props}
      >
        {children}
      </aside>
    </>,
    slot,
  );
}
